"use client";

import type { ReactNode } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import HeroVariant5 from "./HeroVariant5";
import type { HomeHeroContent } from "./HeroVariant5";
import RouteOneFinancingButton from "./finance/RouteOneFinancingButton";
interface HomePageClientProps {
  shoppingLinks?: ReactNode;
  data: {
    hero?: HomeHeroContent;
    findYourCar?: unknown;
    carsWeLoveForYou?: unknown;
    personalShopper?: unknown;
  } | null;
  bodyStyleCounts: BodyStyleCounts | null | undefined;
  underTwentyKCount?: number | null;
}
interface BodyStyleCount {
  value?: string;
  count?: number;
}
interface BodyStyleCounts {
  counts?: BodyStyleCount[];
}
interface VehicleStyle {
  label: string;
  filterValue: string;
  href: string;
  img: string;
  alt: string;
}
const VEHICLE_STYLES: VehicleStyle[] = [{
  label: "SUVs",
  filterValue: "SUV",
  href: "/cars/collections/suvs",
  img: "/media/suv.png",
  alt: "White SUV"
}, {
  label: "Sedans",
  filterValue: "Sedan",
  href: "/cars?bodystyle=Sedan",
  img: "/media/sedan.png",
  alt: "White sedan"
}, {
  label: "Trucks",
  filterValue: "Truck",
  href: "/cars/collections/trucks",
  img: "/media/truck.png",
  alt: "White pickup truck"
}, {
  label: "EVs",
  filterValue: "Electric",
  href: "/cars?bodystyle=Electric",
  img: "/media/electric.png",
  alt: "White electric vehicle"
}, {
  label: "Coupes",
  filterValue: "Coupe",
  href: "/cars?bodystyle=Coupe",
  img: "/media/coupes.png",
  alt: "White coupe"
}, {
  label: "Hatchbacks",
  filterValue: "Hatchback",
  href: "/cars?bodystyle=Hatchback",
  img: "/media/hatchback.png",
  alt: "White hatchback"
}];
const WHY_THISCAR_POINTS = [{
  number: "01",
  title: "No haggling.",
  text: "One honest price, right up front. Everyone gets our best price."
}, {
  number: "02",
  title: "No dealer fees. Zero. Zip. Nada.",
  text: "No doc fee. No prep fee. No magic air in your tires. And no surprises at the finish line."
}, {
  number: "03",
  title: "Auction cars? Not here!",
  text: "Unlike car dealerships, THIScar never sells auction cars. We source every vehicle we sell directly from the consumer who owns it. Better cars in means better cars out."
}, {
  number: "04",
  title: "Changed your mind? Bring it back.",
  text: "Eligible domestic retail purchases include a 3-day/150-mile return option and a day 4–30/1,000-mile exchange option. A $1,500 restocking fee and additional terms apply."
}];
function getBodyStyleCount(bodyStyleCounts: BodyStyleCounts | null | undefined, filterValue: string): number | null {
  const match = bodyStyleCounts?.counts?.find(style => style.value?.toLowerCase() === filterValue.toLowerCase());
  return typeof match?.count === "number" ? match.count : null;
}
function formatAvailableCount(count: number | null): string {
  return count === null ? "Shop now" : `${count.toLocaleString()} available`;
}
export default function HomePageClient({
  data,
  bodyStyleCounts,
  underTwentyKCount,
  shoppingLinks
}: HomePageClientProps) {
  const heroContent = data?.hero;
  const trackVehicleSelection = (vehicleType: string, source: string, count: number | null) => {
    try {
      sendGTMEvent({
        event: "select_item",
        item_list_name: "vehicle_body_styles",
        item_name: vehicleType,
        vehicle_type: vehicleType,
        user_intent: "browse",
        page_section: source,
        item_count: count ?? undefined,
        list_page: 1,
        list_sort: "home_body_styles"
      });
    } catch (error) {
      console.error("Analytics tracking error:", error);
    }
  };
  const trackViewAllClick = () => {
    try {
      sendGTMEvent({
        event: "view_item_list",
        item_list_name: "all_vehicles",
        page_section: "vehicle_styles",
        user_intent: "browse",
        list_page: 1,
        list_sort: "home_view_all"
      });
    } catch (error_0) {
      console.error("Analytics tracking error:", error_0);
    }
  };
  const trackFinancingClick = () => {
    try {
      sendGTMEvent({
        event: "select_promotion",
        promotion_name: "financing_application",
        creative_slot: "quick_paths_finance",
        promotion_id: "finance_quick_path",
        page_section: "quick_paths",
        user_intent: "finance",
        value: 25
      });
    } catch (error_1) {
      console.error("Analytics tracking error:", error_1);
    }
  };
  const trackSellingClick = () => {
    try {
      sendGTMEvent({
        event: "select_promotion",
        promotion_name: "sell_your_car_service",
        creative_slot: "quick_paths_sell",
        promotion_id: "sell_car_quick_path",
        page_section: "quick_paths",
        user_intent: "sell",
        value: 25
      });
    } catch (error_2) {
      console.error("Analytics tracking error:", error_2);
    }
  };
  const trackPriceBandClick = (label: string, destination: string) => {
    try {
      sendGTMEvent({
        event: "select_promotion",
        promotion_name: label,
        creative_slot: destination,
        page_section: "price_bands",
        user_intent: "browse"
      });
    } catch (error_3) {
      console.error("Analytics tracking error:", error_3);
    }
  };
  return <div className="t-flex t-min-h-screen t-flex-col t-bg-white">
            <HeroVariant5 content={heroContent} />
            {shoppingLinks}

            {/* Quick paths: finance or sell */}
            <section className="t-relative t-z-10 t--mt-6 t-px-4 sm:t--mt-8 sm:t-px-6 lg:t-px-8" aria-label="Quick links to financing and selling your car">
                <div className="t-mx-auto t-grid t-max-w-screen-2xl t-grid-cols-1 t-divide-y t-divide-gray-100 t-bg-white t-shadow-[0_10px_35px_rgba(28,29,31,0.14)] md:t-grid-cols-2 md:t-divide-x md:t-divide-y-0">
                    <RouteOneFinancingButton buttonId="home-finance-quick-path" pageSection="home_quick_paths" onClick={trackFinancingClick} className="t-group t-flex t-w-full t-items-center t-justify-between t-gap-4 t-bg-transparent t-p-6 t-text-left focus:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-inset focus-visible:t-ring-automotive-primary sm:t-p-8" ariaLabel="Get pre-qualified for financing with RouteOne (opens in a new tab)">
                        <span>
                            <span className="t-mb-2 t-block t-text-xs t-font-black t-uppercase t-tracking-[0.13em] t-text-automotive-primary">
                                Finance
                            </span>
                            <span className="t-block t-text-xl t-font-normal t-leading-snug t-text-automotive-dark sm:t-text-2xl">
                                Getting pre-qualified couldn&apos;t be easier.
                            </span>
                        </span>
                        <ArrowRight className="t-h-6 t-w-6 t-shrink-0 t-text-automotive-primary t-transition-transform group-hover:t-translate-x-1" aria-hidden="true" />
                    </RouteOneFinancingButton>

                    <Link href="/sell-my-car" onClick={trackSellingClick} className="t-group t-flex t-items-center t-justify-between t-gap-4 t-p-6 sm:t-p-8" aria-label="Get an offer to sell or trade your car">
                        <span>
                            <span className="t-mb-2 t-block t-text-xs t-font-black t-uppercase t-tracking-[0.13em] t-text-automotive-primary">
                                Sell us your car
                            </span>
                            <span className="t-block t-text-xl t-font-normal t-leading-snug t-text-automotive-dark sm:t-text-2xl">
                                A real offer from a real appraiser.
                            </span>
                            <span className="t-mt-2 t-block t-text-sm t-text-gray-500">
                                We&apos;ll buy yours&mdash;even if you don&apos;t buy ours.
                            </span>
                        </span>
                        <ArrowRight className="t-h-6 t-w-6 t-shrink-0 t-text-automotive-primary t-transition-transform group-hover:t-translate-x-1" aria-hidden="true" />
                    </Link>
                </div>
            </section>

            {/* Body styles */}
            <section className="t-px-4 t-pb-10 t-pt-16 sm:t-px-6 lg:t-px-8 lg:t-pb-14 lg:t-pt-24" aria-labelledby="vehicle-styles-heading">
                <div className="t-mx-auto t-max-w-screen-2xl">
                    <div className="t-mb-6 sm:t-mb-8">
                        <p className="t-mb-2 t-text-xs t-font-black t-uppercase t-tracking-[0.14em] t-text-automotive-primary">
                            Start shopping
                        </p>
                        <h2 id="vehicle-styles-heading" className="t-text-3xl t-font-normal t-leading-tight t-text-automotive-dark sm:t-text-4xl">
                            Find your kind of car.
                        </h2>
                    </div>

                    <nav className="vehicle-styles-grid t-grid t-grid-cols-2 sm:t-grid-cols-3 lg:t-grid-cols-6" aria-label="Vehicle body styles">
                        {VEHICLE_STYLES.map(vehicle => {
            const count_0 = getBodyStyleCount(bodyStyleCounts, vehicle.filterValue);
            return <Link key={vehicle.filterValue} href={vehicle.href} onClick={() => trackVehicleSelection(vehicle.filterValue, "vehicle_styles", count_0)} className="t-group t-block t-pb-5 t-pl-4 t-pr-4 t-pt-4 first:t-pt-0 [&:nth-child(-n+2)]:t-pt-0 sm:[&:nth-child(-n+3)]:t-pt-0 lg:[&:nth-child(n)]:t-pt-5" aria-label={`Shop ${vehicle.label}`}>
                                    <span className="t-relative t-block t-h-24 t-w-full sm:t-h-28">
                                        <Image src={vehicle.img} alt={vehicle.alt} fill className="t-object-contain t-transition-transform t-duration-200 group-hover:t-scale-105" sizes="(max-width: 640px) 42vw, (max-width: 1024px) 28vw, 14vw" />
                                    </span>
                                    <span className="t-mt-2 t-flex t-items-center t-justify-between t-gap-2">
                                        <span>
                                            <span className="t-block t-text-base t-font-bold t-text-automotive-dark">
                                                {vehicle.label}
                                            </span>
                                            <span className="t-mt-1 t-block t-text-xs t-font-semibold t-text-gray-500">
                                                {formatAvailableCount(count_0)}
                                            </span>
                                        </span>
                                        <ArrowRight className="t-h-4 t-w-4 t-shrink-0 t-text-automotive-primary t-transition-transform group-hover:t-translate-x-0.5" aria-hidden="true" />
                                    </span>
                                </Link>;
          })}
                    </nav>
                </div>
            </section>

            {/* Shop under $20k */}
            <section className="t-px-4 t-pb-2 t-pt-8 sm:t-px-6 lg:t-px-8">
                <div className="t-mx-auto t-max-w-[1216px] t-border-l-[7px] t-border-automotive-cta t-bg-brand-surface t-py-7 t-pl-6 t-pr-4 sm:t-pl-9 sm:t-pr-6 lg:t-pr-8">
                    <div className="t-flex t-flex-col t-items-start t-gap-6 sm:t-flex-row sm:t-items-center sm:t-justify-between">
                        <div>
                            <p className="t-mb-1 t-text-[10px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-primary">
                                Shop by price
                            </p>
                            <h2 className="t-text-2xl t-font-normal t-leading-snug t-text-automotive-dark sm:t-text-[30px]">
                                {typeof underTwentyKCount === "number" ? `${underTwentyKCount.toLocaleString()} vehicles under $20,000.` : "More vehicles under $20,000."}
                            </h2>
                        </div>
                        <Link href="/cars/collections/under-20000" onClick={() => trackPriceBandClick("shop_under_20k", "/cars/collections/under-20000")} className="t-inline-flex t-w-full t-shrink-0 t-items-center t-justify-center t-gap-2 t-bg-automotive-primary t-px-5 t-py-3.5 t-text-[13px] t-font-black t-text-white t-transition-all hover:t-translate-x-1 hover:t-bg-automotive-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 sm:t-w-auto">
                            Shop under $20k
                            <ArrowRight className="t-h-4 t-w-4" aria-hidden="true" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Recent markdowns */}
            <section className="t-px-4 t-pb-8 t-pt-2 sm:t-px-6 lg:t-px-8">
                <div className="t-mx-auto t-max-w-[1216px] t-border-l-[7px] t-border-automotive-primary t-bg-automotive-cta t-py-7 t-pl-6 t-pr-4 sm:t-pl-9 sm:t-pr-6 lg:t-pr-8">
                    <div className="t-flex t-flex-col t-items-start t-gap-6 sm:t-flex-row sm:t-items-center sm:t-justify-between">
                        <div>
                            <p className="t-mb-1 t-text-[10px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-primary">
                                Recent markdowns
                            </p>
                            <h2 className="t-text-2xl t-font-normal t-leading-snug t-text-brand-ink sm:t-text-[30px]">
                                Everybody likes a deal.
                            </h2>
                            <span className="t-mt-1 t-block t-text-[13px] t-text-brand-ink/70">
                                Get yours before it&apos;s gone.
                            </span>
                        </div>
                        <Link href="/cars" onClick={() => trackPriceBandClick("shop_recent_markdowns", "/cars")} className="t-inline-flex t-w-full t-shrink-0 t-items-center t-justify-center t-gap-2 t-bg-automotive-primary t-px-5 t-py-3.5 t-text-[13px] t-font-black t-text-white t-transition-all hover:t-translate-x-1 hover:t-bg-automotive-secondary focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 sm:t-w-auto">
                            Shop recent markdowns
                            <ArrowRight className="t-h-4 t-w-4" aria-hidden="true" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why THIScar */}
            <section className="t-bg-automotive-primary t-px-4 t-py-16 sm:t-px-6 lg:t-px-16 lg:t-py-24" aria-labelledby="why-thiscar-heading">
                <div className="t-mx-auto t-max-w-screen-2xl">
                    <div className="t-mb-12 lg:t-mb-16">
                        <p className="t-mb-3 t-text-[11px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-cta">
                            Why THIScar
                        </p>
                        <h2 id="why-thiscar-heading" className="t-text-3xl t-font-normal t-leading-[1.2] t-text-white sm:t-text-4xl sm:t-leading-[1.2] lg:t-text-[52px] lg:t-leading-[1.2]">
                            We didn&apos;t like going to car dealerships either.
                            <span className="t-mt-2 t-block sm:t-mt-3">So we fixed it.</span>
                        </h2>
                    </div>

                    <div className="t-grid t-grid-cols-1 t-gap-8 sm:t-grid-cols-2 lg:t-grid-cols-4 lg:t-gap-10">
                        {WHY_THISCAR_POINTS.map(point => <article key={point.number} className="t-border-t t-border-white/30 t-pt-5">
                                <span className="t-mb-3 t-block t-text-[11px] t-font-black t-text-automotive-cta" aria-hidden="true">
                                    {point.number}
                                </span>
                                <h3 className="t-text-xl t-font-normal t-text-white">{point.title}</h3>
                                <p className="t-mt-3 t-text-[15px] t-leading-relaxed t-text-white">{point.text}</p>
                            </article>)}
                    </div>
                </div>
            </section>

            {/* Sell your car story panel */}
            <section aria-labelledby="sell-story-heading">
                <div className="t-grid t-grid-cols-1 lg:t-grid-cols-[1.2fr_1fr]">
                    <div className="t-relative t-h-[340px] t-w-full sm:t-h-[420px] lg:t-h-[640px]">
                        <Image src="/imgs/home/car-hauler.jpg" alt="A THIScar hauler truck delivering a car in front of a THIScar location" fill className="t-object-cover" sizes="(max-width: 1024px) 100vw, 55vw" />
                    </div>
                    <div className="t-flex t-flex-col t-justify-center t-px-6 t-py-16 sm:t-px-10 lg:t-px-16 lg:t-py-24">
                        <Image src="/imgs/home/sell-us-your-car-logo.png" alt="THIScar.com — Sell Us Your Car!" width={310} height={94} className="t-mb-6 t-h-auto t-w-[220px] sm:t-w-[260px] lg:t-w-[310px]" />
                        <p className="t-text-base t-font-normal t-uppercase t-text-automotive-primary">Sell us your car</p>
                        <h2 id="sell-story-heading" className="t-mt-2 t-text-3xl t-font-normal t-leading-[1.1] t-text-automotive-dark sm:t-text-4xl lg:t-text-[45px]">
                            A real offer.
                            <br />
                            A real person.
                            <br />
                            A really easy process.
                        </h2>
                        <p className="t-mt-4 t-max-w-[460px] t-text-base t-leading-relaxed t-text-gray-600">
                            Tell us about your car. We&apos;ll give you a competitive offer, and we&apos;ll handle
                            everything from there. No salespeople. No pressure. No problem.
                        </p>
                        <Link href="/sell-my-car" onClick={trackSellingClick} className="t-group t-mt-6 t-inline-flex t-w-fit t-items-center t-gap-2.5 t-text-[15px] t-font-black t-text-automotive-primary">
                            Get your offer
                            <ArrowRight className="t-h-4 t-w-4 t-transition-transform group-hover:t-translate-x-0.5" aria-hidden="true" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Financing panel */}
            <section className="t-px-4 t-py-16 sm:t-px-6 lg:t-px-8 lg:t-py-[120px]" aria-labelledby="financing-heading">
                <div className="t-mx-auto t-grid t-max-w-screen-2xl t-grid-cols-1 t-gap-8 lg:t-grid-cols-[1.35fr_1fr] lg:t-gap-x-[140px]">
                    <div>
                        <p className="t-text-[11px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-primary">
                            Financing, simplified
                        </p>
                        <h2 id="financing-heading" className="t-mt-3 t-text-3xl t-font-normal t-leading-[1.08] t-text-automotive-dark sm:t-text-4xl lg:t-text-[47px]">
                            Financing your car
                            <br />
                            just got easier.
                        </h2>
                    </div>
                    <div className="t-flex t-flex-col t-justify-center">
                        <p className="t-text-[17px] t-leading-relaxed t-text-gray-600">
                            We have incredibly competitive financing programs for almost every type of credit. No
                            dealership games here&mdash;just straightforward answers to help you get the car you
                            deserve.
                        </p>
                        <Link href="/pre-qualify" onClick={trackFinancingClick} className="t-group t-mt-5 t-inline-flex t-w-fit t-items-center t-gap-2.5 t-text-[15px] t-font-black t-text-automotive-primary">
                            Get pre-approved
                            <ArrowRight className="t-h-4 t-w-4 t-transition-transform group-hover:t-translate-x-0.5" aria-hidden="true" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Bring the family */}
            <section className="t-bg-automotive-cta t-px-4 t-py-14 sm:t-px-6 lg:t-px-8 lg:t-py-[76px]" aria-labelledby="family-heading">
                <div className="t-mx-auto t-grid t-max-w-screen-2xl t-grid-cols-1 t-gap-6 lg:t-grid-cols-2">
                    <div>
                        <p className="t-text-[11px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-primary">
                            Bring the whole family
                        </p>
                        <h2 id="family-heading" className="t-mt-2 t-text-3xl t-font-normal t-leading-tight t-text-brand-ink sm:t-text-4xl">
                            Car shopping can be fun for them, too.
                        </h2>
                    </div>
                    <div>
                        <p className="t-text-base t-leading-relaxed t-text-brand-ink/80">
                            We&apos;ve got free ice cream, toy cars for the kids, and a movie theater if you have a
                            few minutes to spare.
                        </p>
                        <Link href="/about" className="t-group t-mt-5 t-inline-flex t-w-fit t-items-center t-gap-2.5 t-text-[13px] t-font-black t-text-automotive-primary">
                            See what makes THIScar different
                            <ArrowRight className="t-h-4 t-w-4 t-transition-transform group-hover:t-translate-x-0.5" aria-hidden="true" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Straight answers */}
            <section className="t-px-4 t-py-16 sm:t-px-6 lg:t-px-8 lg:t-py-[105px]" aria-labelledby="resources-heading">
                <div className="t-mx-auto t-grid t-max-w-screen-2xl t-grid-cols-1 t-gap-8 lg:t-grid-cols-[1.35fr_1fr] lg:t-gap-x-[140px]">
                    <div>
                        <p className="t-text-[11px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-primary">
                            Straight answers
                        </p>
                        <h2 id="resources-heading" className="t-mt-3 t-text-3xl t-font-normal t-leading-[1.08] t-text-automotive-dark sm:t-text-4xl lg:t-text-[46px]">
                            Know more before
                            <br />
                            you buy.
                        </h2>
                    </div>
                    <div className="t-flex t-flex-col t-justify-center">
                        <p className="t-text-[17px] t-leading-relaxed t-text-gray-600">
                            Clear guidance about used-car pricing, inspections, financing, dealer fees and buying
                            from outside Texas.
                        </p>
                        <Link href="/faqs" className="t-group t-mt-5 t-inline-flex t-w-fit t-items-center t-gap-2.5 t-text-[13px] t-font-black t-text-automotive-primary">
                            Explore THIScar Resources
                            <ArrowRight className="t-h-4 t-w-4 t-transition-transform group-hover:t-translate-x-0.5" aria-hidden="true" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="t-bg-brand-surface t-px-4 t-py-16 sm:t-px-6 lg:t-px-8 lg:t-py-[104px]" aria-labelledby="final-cta-heading">
                <div className="t-mx-auto t-max-w-screen-2xl t-text-center">
                    <p className="t-text-[11px] t-font-black t-uppercase t-tracking-[0.16em] t-text-automotive-primary">
                        Ready when you are
                    </p>
                    <h2 id="final-cta-heading" className="t-mx-auto t-mt-3 t-max-w-4xl t-text-3xl t-font-normal t-leading-[1.08] t-text-automotive-dark sm:t-text-4xl lg:t-text-[48px]">
                        Life&apos;s too short for dealership games.
                        <br />
                        Get THIScar.
                    </h2>
                    <Link href="/cars" onClick={trackViewAllClick} className="t-group t-mt-6 t-inline-flex t-items-center t-gap-2.5 t-text-[15px] t-font-black t-text-automotive-primary">
                        Shop all cars
                        <ArrowRight className="t-h-4 t-w-4 t-transition-transform group-hover:t-translate-x-0.5" aria-hidden="true" />
                    </Link>
                </div>
            </section>
        </div>;
}